/**
 * component-cart-page.js
 * Custom element <edj-cart-page> — interactivité de la page panier.
 * Lot 5 Batch B — EDJ Shopify theme
 *
 * Comportements :
 *   - Qty +/- via /cart/change.js (line index 1-based)
 *   - Input qty au blur/Enter : update qty
 *   - Bouton "Retirer" (a[data-action="remove-line"]) : preventDefault → qty=0
 *   - Re-render via Section Rendering API ?section_id=main-cart
 *   - Si cart vide après update → reload pour empty state propre
 *   - Code promo input : Enter sur input OU click bouton "Appliquer" → /checkout?discount=CODE
 *     (NB : le wrapper [data-action="apply-discount"] est un <div>, pas un <form>,
 *     pour éviter le nested-form bug avec EdjCartForm parent — fix Batch B itération)
 *   - Garde le scroll position
 *   - Émet cart:updated pour sync drawer/header (Batch C)
 */
(function () {
  'use strict';

  const SECTION_ID = 'main-cart';
  const SECTION_RECOS = 'cart-page-recommendations';
  const ENDPOINT_CHANGE = '/cart/change.js';
  const ENDPOINT_RECOS = '/recommendations/products';

  function clearChildren(el) {
    while (el && el.firstChild) el.removeChild(el.firstChild);
  }

  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  class EdjCartPage extends HTMLElement {
    constructor() {
      super();
      this.boundOnClick = this._onClick.bind(this);
      this.boundOnSubmit = this._onSubmit.bind(this);
      this.boundOnChange = this._onQtyInputChange.bind(this);
      this.boundOnKeydown = this._onKeydown.bind(this);
      this._isLoading = false;
    }

    connectedCallback() {
      this.addEventListener('click', this.boundOnClick);
      this.addEventListener('submit', this.boundOnSubmit);
      this.addEventListener('change', this.boundOnChange);
      this.addEventListener('keydown', this.boundOnKeydown);
      this._hydrateRecommendations();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundOnClick);
      this.removeEventListener('submit', this.boundOnSubmit);
      this.removeEventListener('change', this.boundOnChange);
      this.removeEventListener('keydown', this.boundOnKeydown);
    }

    _onClick(e) {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;

      if (action === 'qty-decrement' || action === 'qty-increment') {
        e.preventDefault();
        this._handleQtyStep(btn, action === 'qty-increment' ? 1 : -1);
        return;
      }

      if (action === 'remove-line') {
        e.preventDefault();
        const row = btn.closest('[data-cart-line]');
        if (row) this._setLineQuantity(parseInt(row.dataset.cartLine, 10), 0);
        return;
      }

      if (action === 'apply-discount-submit') {
        e.preventDefault();
        const wrapper = btn.closest('[data-action="apply-discount"]');
        if (wrapper) this._applyDiscount(wrapper);
        return;
      }
    }

    _onSubmit(e) {
      // Le form parent #EdjCartForm utilise method="post" action="/cart" pour le
      // fallback no-JS d'update qty. On ne fait rien ici.
    }

    _onKeydown(e) {
      if (e.key !== 'Enter') return;
      const input = e.target.closest('[data-cart-discount-input]');
      if (!input) return;
      e.preventDefault();
      e.stopPropagation();
      const wrapper = input.closest('[data-action="apply-discount"]');
      if (wrapper) this._applyDiscount(wrapper);
    }

    _onQtyInputChange(e) {
      const input = e.target.closest('[data-cart-quantity-input]');
      if (!input) return;
      const row = input.closest('[data-cart-line]');
      if (!row) return;
      const newQty = Math.max(0, parseInt(input.value, 10) || 0);
      const lineIndex = parseInt(row.dataset.cartLine, 10);
      this._setLineQuantity(lineIndex, newQty);
    }

    _handleQtyStep(btn, delta) {
      const row = btn.closest('[data-cart-line]');
      if (!row) return;
      const input = row.querySelector('[data-cart-quantity-input]');
      const current = input ? (parseInt(input.value, 10) || 0) : 0;
      const next = Math.max(0, current + delta);
      const lineIndex = parseInt(row.dataset.cartLine, 10);
      this._setLineQuantity(lineIndex, next);
    }

    _applyDiscount(form) {
      const input = form.querySelector('[data-cart-discount-input]');
      const code = (input && input.value || '').trim();
      if (!code) {
        if (input) input.focus();
        return;
      }
      const url = '/checkout?discount=' + encodeURIComponent(code);
      window.location.href = url;
    }

    async _setLineQuantity(lineIndex, quantity) {
      if (this._isLoading) return;
      if (!Number.isFinite(lineIndex) || lineIndex < 1) return;

      this._setLoading(true);
      const scrollY = window.scrollY;

      try {
        const res = await fetch(ENDPOINT_CHANGE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            line: lineIndex,
            quantity: quantity,
            sections: SECTION_ID,
            sections_url: window.location.pathname,
          }),
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error((data && data.description) || 'Erreur mise à jour panier');
        }

        // Cart vide → on reload pour afficher proprement l'empty state
        // (qui est dans une branche différente du DOM).
        if (data.item_count === 0) {
          window.location.reload();
          return;
        }

        // Re-render section via sections renvoyés par /cart/change.js
        if (data.sections && data.sections[SECTION_ID]) {
          this._renderSection(data.sections[SECTION_ID]);
        } else {
          // Fallback : Section Rendering API explicite
          await this._refetchSection();
        }

        // Notifie drawer + header (count badge)
        this._emitCartUpdated(data);
      } catch (err) {
        console.error('[EDJ cart] update error:', err);
        this._setLoading(false);
        return;
      }

      this._setLoading(false);
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    }

    async _refetchSection() {
      try {
        const url = window.location.pathname + '?section_id=' + SECTION_ID;
        const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
        if (!res.ok) return;
        const html = await res.text();
        this._renderSection(html);
      } catch (err) {
        console.error('[EDJ cart] refetch error:', err);
      }
    }

    _renderSection(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      // /cart?section_id=main-cart renvoie la section seule (pas dans <html>)
      const newRoot = doc.querySelector('edj-cart-page') || doc.body.firstElementChild;
      if (!newRoot) return;

      // Remplace inner pour préserver l'event listener du custom element
      const newInner = newRoot.querySelector('.edj-cart-page__inner');
      const currentInner = this.querySelector('.edj-cart-page__inner');
      if (newInner && currentInner) {
        currentInner.replaceWith(newInner);
        // Re-hydrate recos (anchor product peut avoir changé)
        this._hydrateRecommendations();
      }
    }

    _setLoading(state) {
      this._isLoading = state;
      this.dataset.loading = state ? 'true' : 'false';
    }

    _emitCartUpdated(data) {
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: data,
        bubbles: true,
      }));
      this._syncHeaderBadge(data);
    }

    _syncHeaderBadge(data) {
      const count = (data && typeof data.item_count === 'number') ? data.item_count : null;
      if (count === null) return;
      document.querySelectorAll('.header__cart-badge').forEach((badge) => {
        if (count > 0) {
          badge.textContent = count;
          badge.setAttribute('aria-label', count + ' articles');
          badge.hidden = false;
        } else {
          badge.hidden = true;
          badge.textContent = '';
        }
      });
      if (count > 0) {
        document.querySelectorAll('.header__cart').forEach((cartLink) => {
          if (!cartLink.querySelector('.header__cart-badge')) {
            const badge = document.createElement('span');
            badge.className = 'header__cart-badge';
            if (cartLink.classList.contains('header__cart--mobile')) {
              badge.className += ' header__cart-badge--mobile';
            }
            badge.textContent = count;
            badge.setAttribute('aria-label', count + ' articles');
            cartLink.appendChild(badge);
          }
        });
      }
    }

    /* ---------------------- Recommendations hydration ---------------------- */

    async _hydrateRecommendations() {
      const block = this.querySelector('[data-cart-page-recos]');
      if (!block) return;
      const list = block.querySelector('[data-cart-page-recos-list]');
      if (!list) return;
      // Skip if bundle_pack already rendered cards
      const existing = list.querySelectorAll('[data-cart-page-reco-card]');
      if (existing.length > 0) return;

      const anchorId = block.dataset.anchorId;
      const intent = block.dataset.intent || 'related';
      const limit = block.dataset.limit || '4';
      const apiIntent = intent === 'complementary' ? 'complementary' : 'related';
      if (!anchorId) return;

      try {
        const url =
          ENDPOINT_RECOS +
          '?product_id=' + encodeURIComponent(anchorId) +
          '&intent=' + apiIntent +
          '&limit=' + encodeURIComponent(limit) +
          '&section_id=' + SECTION_RECOS;
        const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
        if (!res.ok) {
          block.hidden = true;
          return;
        }
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const inner = doc.querySelector('[data-cart-page-recos-hydrated]');
        if (!inner) {
          block.hidden = true;
          return;
        }
        const cards = inner.querySelectorAll('[data-cart-page-reco-card]');
        if (cards.length === 0) {
          block.hidden = true;
          return;
        }
        clearChildren(list);
        cards.forEach((c) => list.appendChild(c.cloneNode(true)));
      } catch (err) {
        console.error('[EDJ cart page] recos hydrate error:', err);
        block.hidden = true;
      }
    }
  }

  if (!customElements.get('edj-cart-page')) {
    customElements.define('edj-cart-page', EdjCartPage);
  }
})();
